import type { AuthUser } from '@centrix/api-client';

interface LocationStateWithOnboarding {
  email?: string;
  token?: string;
}

function getLocationStateValue(
  locationState: unknown,
  key: keyof LocationStateWithOnboarding
): string | undefined {
  if (!locationState || typeof locationState !== 'object') {
    return undefined;
  }

  const value = (locationState as LocationStateWithOnboarding)[key]?.trim();
  return value ? value : undefined;
}

export function resolveOnboardingEmail({
  locationState,
  searchParams,
  currentUser,
}: {
  locationState: unknown;
  searchParams: URLSearchParams;
  currentUser?: Pick<AuthUser, 'email'> | null;
}): string {
  return (
    getLocationStateValue(locationState, 'email') ??
    searchParams.get('email')?.trim() ??
    currentUser?.email?.trim() ??
    ''
  );
}

export function resolveOnboardingToken({
  locationState,
  searchParams,
}: {
  locationState: unknown;
  searchParams: URLSearchParams;
}): string {
  return getLocationStateValue(locationState, 'token') ?? searchParams.get('token')?.trim() ?? '';
}

// Session flag set when signup succeeds, cleared when user returns to signup.
// VerifyEmailPage uses this to block direct access (back/forward navigation,
// shared URLs) so the OTP screen cannot be re-opened without a fresh signup.
const SIGNUP_IN_FLIGHT_KEY = 'centrix_signup_in_flight';

function getSession(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function markSignupInFlight(): void {
  getSession()?.setItem(SIGNUP_IN_FLIGHT_KEY, '1');
}

export function clearSignupInFlight(): void {
  getSession()?.removeItem(SIGNUP_IN_FLIGHT_KEY);
}

export function isSignupInFlight(): boolean {
  return getSession()?.getItem(SIGNUP_IN_FLIGHT_KEY) === '1';
}

// Per-email flag set when OTP verification succeeds. The OTP screen uses it to
// block re-entry, and the Sector Selection screen uses it to verify the user
// actually passed OTP before showing the secure layout (guards against
// browser back/forward restoring the screen without re-verifying).
const VERIFIED_SESSION_PREFIX = 'centrix_email_verified:';

function getVerifiedSessionKey(email: string): string {
  return `${VERIFIED_SESSION_PREFIX}${email.trim().toLowerCase()}`;
}

export function isEmailMarkedVerified(email: string): boolean {
  if (!email) return false;
  return getSession()?.getItem(getVerifiedSessionKey(email)) === '1';
}

export function markEmailVerified(email: string): void {
  if (!email) return;
  // sessionStorage may be unavailable (private mode); getSession swallows that.
  getSession()?.setItem(getVerifiedSessionKey(email), '1');
}
export function setRegistrationType(registrationType: string): void {
  if (!registrationType) return;
  // sessionStorage may be unavailable (private mode); getSession swallows that.
  getSession()?.setItem('registrationType', registrationType);
}

function clearAllEmailVerified(session: Storage): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < session.length; i += 1) {
    const key = session.key(i);
    if (key && key.startsWith(VERIFIED_SESSION_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => session.removeItem(key));
}

// Clears every piece of signup security state: the in-flight flag and all
// per-email verified flags. Called when the user lands back on step 1 of the
// signup flow (direct visit or browser Back) so that pressing Forward cannot
// restore a previously verified session and re-open the secure Sector
// Selection screen without re-verifying the OTP.
export function resetSignupSession(): void {
  const session = getSession();
  if (!session) return;
  session.removeItem(SIGNUP_IN_FLIGHT_KEY);
  clearAllEmailVerified(session);
}

export function buildOnboardingPath(
  path: string,
  options: {
    email?: string;
    params?: Record<string, string | undefined>;
  } = {}
): string {
  const searchParams = new URLSearchParams();

  if (options.email?.trim()) {
    searchParams.set('email', options.email.trim());
  }

  Object.entries(options.params ?? {}).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}
